// src/pages/MemberDashboard.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Paper,
  Typography,
  Avatar,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  EventAvailableOutlined,
  GroupsOutlined,
  HistoryOutlined,
  TrendingUp,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  SendOutlined,
  PendingActionsOutlined,
  VerifiedOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { getData } from "../services/request";

const currency = (value, symbol = "NGN") =>
  `${symbol} ${Number(value || 0).toLocaleString("en-US")}`;

// ✅ Helper: Response ko array mein convert karein
const toJoinedGroups = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.groups)) return response.groups;
  return response?.id ? [response] : [];
};

// 📌 DUMMY DATA (FALLBACK AGAR API FAIL HO JAYE)
const fallbackStats = {
  joinedGroups: 3,
  upcomingContributions: 15000,
  spentTotal: 8500,
  activeGroups: 2,
  requestedCount: 5,
  acceptedCount: 3,
  pendingCount: 2,
};

const MemberDashboard = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // ✅ API se aane wala Data
  const [joinedGroups, setJoinedGroups] = useState([]);
  const [stats, setStats] = useState(fallbackStats);

  // Stored User
  const storedUser =
    user || JSON.parse(localStorage.getItem("userData") || "{}");
  const symbol =
    storedUser?.country?.symbol || storedUser?.country?.currency || "NGN";
  const displayName = storedUser?.fullName || storedUser?.full_name || "Member";
  const userRole = storedUser?.userType || "groupUser";

  const goTo = (path) => {
    setMobileNavOpen(false);
    navigate(path);
  };

  const logout = () => {
    ["accessToken", "userData", "deviceId", "notifications", "nin"].forEach(
      (key) => localStorage.removeItem(key),
    );
    navigate("/", { replace: true });
  };

  // ✅ API Call: fetch_joined se stats nikaalein
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getData("/api/group/fetch_joined");
        const groups = toJoinedGroups(response);
        setJoinedGroups(groups);

        // ✅ Status ke hisaab se count nikaalein
        const requestedCount = groups.length; // Total requests
        const acceptedCount = groups.filter(
          (g) => g?.status === "Approved" || g?.status === "GroupAdmin",
        ).length;
        const pendingCount = groups.filter(
          (g) => g?.status === "Panding" || g?.status === "Pending",
        ).length;

        setStats((prev) => ({
          ...prev,
          joinedGroups: acceptedCount,
          requestedCount: requestedCount,
          acceptedCount: acceptedCount,
          pendingCount: pendingCount,
          activeGroups: acceptedCount,
        }));
      } catch (error) {
        console.error("Error fetching joined groups:", error);
        setJoinedGroups([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <Box
      sx={{ minHeight: "100vh", backgroundColor: "#f6f7fb", color: "#1d2433" }}
    >
      {/* Navbar */}
      <Navbar
        onMenuClick={() => setMobileNavOpen(true)}
        onNavigate={goTo}
        onLogout={logout}
        user={{ fullName: displayName, userType: userRole }}
      />

      {/* Sidebar */}
      <Sidebar
        role={userRole}
        activePath={location.pathname}
        onNavigate={goTo}
        mobileOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{ ml: { xs: 0, md: "248px" }, p: { xs: 2, sm: 3, md: 5 } }}
      >
        <Box sx={{ maxWidth: 1440, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h4"
              sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800 }}
            >
              Member Dashboard
            </Typography>
            <Typography sx={{ color: "#7a8394", mt: 0.7 }}>
              Stay on top of your shared savings commitments.
            </Typography>
          </Box>

          {/* ✅ NAYA SECTION: Request, Accepted, Pending Counts */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(3, 1fr)",
              },
              gap: 2,
              mb: 4,
            }}
          >
            <StatCard
              title="Requested Groups"
              value={loading ? "..." : stats.requestedCount}
              caption="Total join requests sent"
              icon={<SendOutlined />}
            />
            <StatCard
              title="Accepted Groups"
              value={loading ? "..." : stats.acceptedCount}
              caption="Requests approved by admin"
              icon={<VerifiedOutlined />}
              accent
            />
            <StatCard
              title="Pending Groups"
              value={loading ? "..." : stats.pendingCount}
              caption="Waiting for admin approval"
              icon={<PendingActionsOutlined />}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Reusable Stat Card
const StatCard = ({ title, value, caption, icon, accent = false }) => (
  <Paper
    sx={{
      p: 2.5,
      minHeight: 142,
      border: "1px solid #e9ebf0",
      borderRadius: 2,
      boxShadow: "none",
      bgcolor: accent ? "#52057b" : "#fff",
      color: accent ? "#fff" : "#1d2433",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
      <Box>
        <Typography
          sx={{
            fontSize: 13,
            color: accent ? "rgba(255,255,255,.75)" : "#7a8394",
            fontWeight: 700,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{ fontSize: { xs: 25, sm: 29 }, fontWeight: 800, mt: 1 }}
        >
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 38,
          height: 38,
          display: "grid",
          placeItems: "center",
          borderRadius: 1.5,
          bgcolor: accent ? "rgba(255,255,255,.14)" : "#f7f1fa",
          color: accent ? "#fff" : "#52057b",
        }}
      >
        {icon}
      </Box>
    </Box>
    <Typography
      sx={{
        color: accent ? "rgba(255,255,255,.7)" : "#7a8394",
        fontSize: 12,
        mt: 2,
      }}
    >
      {caption}
    </Typography>
  </Paper>
);

export default MemberDashboard;
